import { MeasuringCategory, ProfileInterface, TeamOnboardingField, UserProfileRole } from 'ts-interfaces'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { useStaging, firebaseDB, profilesDB, firebaseApp } from '../../libs/firebase'
import { produce } from 'immer'
import { doc, getDoc, getDocs, getDocsFromServer, query, where, collection, updateDoc, increment, FieldPath } from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import { generateID, getUserLocationFromIPdata } from "../helper";
import { updateProfile } from '../../libs/util'
import { firebaseHelper } from 'ui'
import { Permissions } from "ts-interfaces";
import { marketingUrls } from "./utils";
import { trackEvent, trackEventGA, trackEventMeta, identifyUser, setTeamProContext } from '../../libs/analyticsManager';
import { takeSignupOrigin } from '../../Home/showrooms/signupOrigin';
import { getProviderSource } from '../../libs/authProviders';
import { seedStarterContent } from './seedStarterContent';


export interface LoginData {
  user: any
  providerId?: string
  _tokenResponse?: any
}

// Options for surfaces other than the website's auth modals (the SketchUp
// panel). Omitted = the website's behaviour, unchanged.
export interface ProfileOptions {
  // Stamped on the `signup` event in every destination and on the new
  // profile's signupSurface. (Signup credits are not granted here: the panel
  // requests them itself on every open — see SketchupLayout.)
  surface?: 'sketchup'
  // False skips the `signin` event for an existing profile: the panel runs
  // this on every open, which is not a sign-in.
  trackSignin?: boolean
  // Checked after the load: false drops the result (the panel's signed-in
  // account changed mid-load), so a stale profile never lands in the store.
  isCurrent?: () => boolean
}

export interface LoginState {
  profile: ProfileInterface | null
  showLogin: any
  
  // Teams PRO access state
  isProUser: boolean
  isTeamsProUser: boolean
  // uid whose isProUser/isTeamsProUser updateTeamsProAccess has finished
  // computing (null until then). Lets a caller wait for the real plan
  // instead of reading the 'free' defaults (SketchUp panel analytics).
  proAccessResolvedUid: string | null
}


let globalLoginStateIsProcessing=false; 
let globalProfileIsProcessing = false;

// The signed-in account can change while a profile load awaits Firestore; a
// result for any other uid is stale and must not land in the store (#4054).
// Signed out mid-load counts as stale. Unknown auth (no app yet, e.g. tests)
// is not, so behaviour is unchanged there.
const isStaleProfile = (profile?: { uid?: string } | null) => {
  if (!firebaseApp || !profile?.uid) return false;
  return getAuth(firebaseApp).currentUser?.uid !== profile.uid;
}

// Everything setProfile(null) resets that belongs to one account.
const NO_ACCOUNT_STATE = {
  firebaseDoc: null,
  permissions: null,
  profile: null,
  isProUser: false,
  isTeamsProUser: false,
  proAccessResolvedUid: null,
}

// A tab signed out from another tab keeps that account's profile (the Bar that
// clears it unmounts first), so drop it before loading a different account.
// showLogin stays, so an open sign-up modal keeps its flow. Only for the
// signed-in account: a late callback for another uid must not clear it.
const dropOtherAccount = (get: any, set: any, uid?: string) => {
  const heldUid = get().profile?.uid
  if (!uid || !heldUid || heldUid === uid) return
  if (firebaseApp && getAuth(firebaseApp).currentUser?.uid !== uid) return
  set(NO_ACCOUNT_STATE)
}

// Team onboarding stamps set locally whose write hasn't landed (`uid:firm:field`).
const unsavedTeamStamps = new Set<string>()


const useLoginStore = (set:any, get:any) => ({
  profile:null,
  showLogin:null,
  processing:false,
  firebaseDoc:null,
  permissions: null,
  
  // Teams PRO access state
  isProUser: false,
  isTeamsProUser: false,
  proAccessResolvedUid: null,

  getPermmissions: async (skipTeamsProUpdate = false): Promise<Permissions | null> => {
    if (get().permissions) return get().permissions
    const uid = get().profile?.uid
    if (get().profile?.uid == null) return null;
    const docRef = doc(firebaseHelper.db, 'permissions', uid);
    const docSnap = await getDoc(docRef);
    // Account changed while awaiting: never cache the previous user's permissions.
    if (get().profile?.uid !== uid) return null;
    if (docSnap.exists()) {
      const data = docSnap.data() as Permissions;
      set({permissions:data})
      // Update Teams PRO access when permissions are loaded (unless we're already updating it)
      if (!skipTeamsProUpdate) {
        get().updateTeamsProAccess()
      }
      return data
    }
    else { return null; }
  },

  // Force re-fetch of permissions, bypassing the in-memory cache.
  // Used after server-side writes (e.g. team creation webhook minting
  // `teamsAdmin`) so the UI picks up the new firm membership without
  // requiring a hard refresh.
  //
  // Atomic update: we fetch the new doc BEFORE touching state, so
  // consumers never observe an intermediate `permissions: null` window.
  // A transient null causes useWorkspaceContext.baseWorkspaces to drop
  // every team from the list, which in turn triggers the reconciliation
  // effect to bounce the active workspace back to Personal.
  refreshPermissions: async (): Promise<Permissions | null> => {
    const uid = get().profile?.uid;
    if (!uid) {
      set({ permissions: null });
      return null;
    }
    const docRef = doc(firebaseHelper.db, 'permissions', uid);
    const docSnap = await getDoc(docRef);
    // Account changed while awaiting: leave the new user's state alone.
    if (get().profile?.uid !== uid) return null;
    if (docSnap.exists()) {
      const data = docSnap.data() as Permissions;
      set({ permissions: data });
      get().updateTeamsProAccess();
      return data;
    }
    set({ permissions: null });
    return null;
  },

  // Re-read the user's profile from Firestore and replace the cached
  // copy. Used after server-side mutations to profile fields (e.g. the
  // team-creation webhook stamping userRole=pro) so Pro-gated UI like
  // the "Upgrade to Pro" button / "Pro Teams" badge / project tab
  // updates without a hard refresh.
  refreshProfile: async (): Promise<any> => {
    const uid = get().profile?.uid;
    if (!uid) return null;
    const q = query(profilesDB, where('uid', '==', uid));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const docSnap = snap.docs[0];
    const next = { id: docSnap.id, ...docSnap.data() } as any;
    get().setProfile(next);
    return next;
  },



  // Update Teams PRO access status
  updateTeamsProAccess: async () => {
    const state = get();
    const profile = state.profile;
    const currentUserUid = profile?.uid;
    
    if (!currentUserUid || !profile) {
      set({
        isProUser: false,
        isTeamsProUser: false,
        proAccessResolvedUid: null,
      });
      setTeamProContext(false);
      return;
    }

    try {
      // Check if user is PRO
      const isProUser = profile && (profile as any)?.userRole === UserProfileRole.PRO;

      // Get permissions if not already loaded
      let permissions = state.permissions;
      if (!permissions) {
        permissions = await get().getPermmissions(true); // Skip teams pro update to prevent infinite loop
      }
      
      // Membership-wide "team Pro" signal for the sharing UI: the user is an
      // ACTIVE DESIGNER in at least one team. This is DECOUPLED from personal
      // Pro (profiles.userRole) — a team designer with no personal sub still
      // gets the team-sharing surface. The teams-collection query is the only
      // source that is both prod-safe (works when the workspaces feature flag
      // is off) and active-roster-aware. Scans ALL the user's team docs.
      let isTeamsProUser = false;
      if (permissions?.teamsDesigner?.length) {
        // Query the teams collection to find an active designer membership.
        try {
          const teamsCollection = collection(firebaseHelper.db, 'teams');
          const teamQuery = query(teamsCollection, where("uid", "==", currentUserUid));
          const teamSnapshot = await getDocs(teamQuery);

          for (const teamDoc of teamSnapshot.docs) {
            const teamData = teamDoc.data();
            const isDesigner =
              teamData?.role === 'designer' ||
              teamData?.role === 'designer_and_admin';
            if (teamData && teamData.status === 'active' && isDesigner) {
              isTeamsProUser = true;
              break; // Found an active designer team — no need to check further
            }
          }
        } catch (error) {
          console.error('Error checking team status:', error);
        }
      }

      // Stale-write guard: this function awaited Firestore (getPermmissions +
      // the teams query), during which the user could have logged out or
      // switched accounts. Bail before writing so we never re-enable the
      // previous user's Pro/team flags after they were cleared.
      if (get().profile?.uid !== currentUserUid) return;

      set({
        // Personal entitlement (drives the "Regular Pro" sharing branch).
        isProUser,
        // Membership-wide team Pro (drives the team-sharing surface).
        isTeamsProUser,
        proAccessResolvedUid: currentUserUid,
      });
      // Keep the analytics team-Pro trait in sync — identify() reads this so the
      // Teams cohort stays visible after userRole is decoupled from team Pro.
      setTeamProContext(isTeamsProUser);

    } catch (error) {
      console.error('Error updating Teams PRO access:', error);
      // Same stale-write guard on the failure path (it follows the same awaits).
      if (get().profile?.uid !== currentUserUid) return;
      set({
        isProUser: false,
        isTeamsProUser: false,
        proAccessResolvedUid: currentUserUid,
      });
      setTeamProContext(false);
    }
  },

  setShowLogin: (showLogin: any)=> {
    if (showLogin && typeof window !== 'undefined') {
      try {
        const pathname = window?.location?.pathname || '';
        const isMarketingPath = marketingUrls.some((url) => {
          if (url.endsWith("*")) {
            const prefix = url.slice(0, -1);
            return pathname === prefix || pathname.startsWith(prefix);
          }
          return pathname === url;
        });

        if (isMarketingPath && !sessionStorage?.getItem("redirectTo")) {
          sessionStorage?.setItem("redirectTo", "/home/library");
        }
      } catch (error) {
        console.warn("[LoginState] Unable to evaluate marketing path for redirect:", error);
      }
    }

    set({showLogin})
  },
  
  setProfile: (profile:any) => {
    set({processing:false})
    if (!profile || !profile?.id) {
      set({ ...NO_ACCOUNT_STATE, processing: false, showLogin: null })
    }
    else {
      set({
        profile:profile,
      })
      // Update Teams PRO access when profile is set
      get().updateTeamsProAccess()
    }
  },
  
  setIncrementRenderCount: async () => {
    if (globalLoginStateIsProcessing==true) return;
    const profile = get().profile
    if (!profile) return;
    try {
      globalLoginStateIsProcessing=true;
      const newProfile:ProfileInterface |any = produce(profile, (draft:ProfileInterface) => {
        draft.renders = draft.renders ? draft.renders + 1 : 1
      })
      await get().updateAndSaveProfile(newProfile)
    }
    finally { globalLoginStateIsProcessing=false; }
  },
  
  //replaces the createProfileIfNotExists function
  // TODO: find instances of createProfileIfNotExists and delete or replace them with this function
  //only useful when we have loginData
  //most important thing.. if i end up createing a profile 
  //Only one scenario where this is a problem -> user signups, then logs out, then logs in really fast. 
  // Persist that the user has seen the one-time Enhance education modal so it
  // never shows again (across reloads and devices). Mirrors setIncrementRenderCount.
  markEnhanceEducationSeen: async () => {
    if (globalLoginStateIsProcessing==true) return;
    const profile = get().profile
    if (!profile || profile.hasSeenEnhanceEduModal) return;
    try {
      globalLoginStateIsProcessing=true;
      const newProfile:ProfileInterface |any = produce(profile, (draft:ProfileInterface) => {
        draft.hasSeenEnhanceEduModal = true
      })
      await get().updateAndSaveProfile(newProfile)
    }
    finally { globalLoginStateIsProcessing=false; }
  },

  // Stamp the dashboard's getting-started checklist as done (completed or
  // dismissed). Idempotent. Returns false ONLY when another profile write held
  // the lock, so the caller can retry rather than lose the stamp — these are
  // whole-profile writes and clobber each other.
  markOnboardingComplete: async (): Promise<boolean> => {
    if (globalLoginStateIsProcessing==true) return false;
    const profile = get().profile
    if (!profile || profile.onboardingCompletedAt) return true;
    try {
      globalLoginStateIsProcessing=true;
      const newProfile:ProfileInterface |any = produce(profile, (draft:ProfileInterface) => {
        draft.onboardingCompletedAt = new Date()
      })
      await get().updateAndSaveProfile(newProfile)
      return true
    }
    finally { globalLoginStateIsProcessing=false; }
  },

  // Stamp that the one-time "Welcome to Mattoboard" video modal has been shown
  // (new signups only — see Home/onboarding). Same contract as markOnboardingComplete:
  // returns false ONLY when another profile write held the lock, so the caller
  // retries instead of losing the stamp.
  markLibraryOnboardingVideoSeen: async (): Promise<boolean> => {
    if (globalLoginStateIsProcessing==true) return false;
    const profile = get().profile
    if (!profile || profile.libraryOnboardingVideoSeenAt) return true;
    try {
      globalLoginStateIsProcessing=true;
      const newProfile:ProfileInterface |any = produce(profile, (draft:ProfileInterface) => {
        draft.libraryOnboardingVideoSeenAt = new Date()
      })
      await get().updateAndSaveProfile(newProfile)
      return true
    }
    finally { globalLoginStateIsProcessing=false; }
  },

  // Count one of a free account's SketchUp panel imports (see
  // sketchup/hooks/useImportAccess). Unlike the mark* actions above this is a
  // field-level atomic increment, not a whole-profile write, and it does NOT
  // take the globalLoginStateIsProcessing lock: that lock skips the call
  // when another write is in flight, which would silently lose a count, and a
  // single-field update cannot clobber the rest of the doc. The in-memory
  // profile is bumped first so the panel's gate is right immediately and
  // later whole-profile writes carry the new value.
  incrementSketchupFreeImports: async () => {
    const profile = get().profile
    if (!profile) return;
    set({
      profile: {
        ...profile,
        sketchupFreeImportsUsed: (profile.sketchupFreeImportsUsed ?? 0) + 1,
      },
    })
    const ref = get().firebaseDoc
    if (!ref) return;
    try {
      await updateDoc(ref, { sketchupFreeImportsUsed: increment(1) })
    } catch (error) {
      console.log("incrementSketchupFreeImports error", error)
    }
  },

  // Stamp that the workspace-switcher callout was dismissed (or the switcher
  // opened). A single-field write, like incrementSketchupFreeImports: team
  // members' profiles carry server-stamped fields (userRole) that a stale
  // whole-profile write could undo. False when not written, so the caller retries.
  markWorkspaceSwitcherTipSeen: async (): Promise<boolean> => {
    const profile = get().profile
    if (!profile) return false;
    const seenAt = new Date()
    set({ profile: { ...profile, workspaceSwitcherTipSeenAt: seenAt } })
    const ref = get().firebaseDoc
    if (!ref) return false;
    try {
      await updateDoc(ref, { workspaceSwitcherTipSeenAt: seenAt })
      return true
    } catch (error) {
      console.log("markWorkspaceSwitcherTipSeen error", error)
      return false
    }
  },

  // One team onboarding stamp (team/onboarding), written field by field like
  // markWorkspaceSwitcherTipSeen. Set locally at once; the write is retried,
  // and a stamp whose write never landed is written again by the next call.
  markTeamOnboarding: async (firmId: string, field: TeamOnboardingField): Promise<boolean> => {
    const profile: ProfileInterface | null = get().profile
    if (!profile || !firmId) return false;
    const key = `${profile.uid}:${firmId}:${field}`
    const existing = profile.teamOnboarding?.[firmId]?.[field]
    if (existing && !unsavedTeamStamps.has(key)) return true;
    const at = existing ?? new Date()
    if (!existing) {
      set({
        profile: {
          ...profile,
          teamOnboarding: {
            ...profile.teamOnboarding,
            [firmId]: { ...profile.teamOnboarding?.[firmId], [field]: at },
          },
        },
      })
    }
    unsavedTeamStamps.add(key)
    for (let attempt = 1; attempt <= 3; attempt++) {
      // Never stamp an account that signed in during a retry gap.
      if (get().profile?.uid !== profile.uid) return false;
      const ref = get().firebaseDoc
      if (ref) {
        try {
          await updateDoc(ref, new FieldPath("teamOnboarding", firmId, field), at)
          unsavedTeamStamps.delete(key)
          return true
        } catch (error) {
          console.log("markTeamOnboarding error", error)
        }
      }
      if (attempt < 3) await new Promise((resolve) => setTimeout(resolve, 500 * 2 ** (attempt - 1)))
    }
    return false
  },

  // Stamp that the library SketchUp promo banner was dismissed. Same contract
  // as markLibraryOnboardingVideoSeen: false ONLY when another profile write
  // held the lock, so the caller retries.
  markSketchupBannerDismissed: async (): Promise<boolean> => {
    if (globalLoginStateIsProcessing==true) return false;
    const profile = get().profile
    if (!profile || profile.sketchupBannerDismissedAt) return true;
    try {
      globalLoginStateIsProcessing=true;
      const newProfile:ProfileInterface |any = produce(profile, (draft:ProfileInterface) => {
        draft.sketchupBannerDismissedAt = new Date()
      })
      await get().updateAndSaveProfile(newProfile)
      return true
    }
    finally { globalLoginStateIsProcessing=false; }
  },

  // Stamp the onboarding arm once a new signup lands on /starting-options
  // (startOptionsExperiment.ts stampPendingStartOptionsArm). Same
  // contract as markLibraryOnboardingVideoSeen: false ONLY when another profile
  // write held the lock, so the caller retries. Never overwrites an arm.
  markOnboardingArm: async (arm: 'start_options' | 'library', experiment: string): Promise<boolean> => {
    if (globalLoginStateIsProcessing==true) return false;
    const profile = get().profile
    if (!profile || profile.onboardingArm) return true;
    try {
      globalLoginStateIsProcessing=true;
      const newProfile:ProfileInterface |any = produce(profile, (draft:ProfileInterface) => {
        draft.onboardingArm = arm
        draft.onboardingExperiment = experiment
        draft.onboardingEligibleAt = new Date()
      })
      await get().updateAndSaveProfile(newProfile)
      return true
    }
    finally { globalLoginStateIsProcessing=false; }
  },


  setOrCreateProfile: async (loginData:LoginData, mailingList=false, opts:ProfileOptions={}) => {
    dropOtherAccount(get, set, loginData?.user?.uid)
    // we can't have a local profile or be processing a change through this codepath
    if (get().profile?.id || get().processing==true) return;
    if (globalLoginStateIsProcessing==true) return;
    
    // note that we are in processing
    set({processing:true})
    globalLoginStateIsProcessing = true
    
    try {
      const resp = await createProfileIfNotExists(loginData, mailingList, opts)
      if (opts.isCurrent && !opts.isCurrent()) return resp;
      if (isStaleProfile(resp?.profile)) return resp;
      if (resp?.profile?.id) {
        set({ firebaseDoc:resp.ref}) 
        get().setProfile(resp.profile) // Use setProfile method to trigger updateTeamsProAccess
      }
      else { 
        get().setProfile(null) // Use setProfile method to clear state properly
      }
      return resp;
    }
    finally { set({processing:false}); globalLoginStateIsProcessing=false; }
  },

  getUserProfile: async (loginData:LoginData) => {
    dropOtherAccount(get, set, loginData?.user?.uid)
    if (get().profile?.id || get().processing==true) return;
    if (globalLoginStateIsProcessing==true) return;
    set({processing:true})
    try {
      globalLoginStateIsProcessing = true
      const resp = await getProfile(loginData)
      if (isStaleProfile(resp?.profile)) return resp;
      if (resp?.profile?.id) { 
        set({ firebaseDoc:resp.ref}) 
        get().setProfile(resp.profile) // Use setProfile method to trigger updateTeamsProAccess
      }
      else { 
        get().setProfile(null) // Use setProfile method to clear state properly
      }
      return resp;
    }
    finally { set({processing:false}); globalLoginStateIsProcessing=false; }
  },

  updateAndSaveProfile: async (profile:ProfileInterface, pic={}) => {
    return new Promise((resolve, reject) => {
      if (get().profile?.uid == null) reject("User not logged in")
      if (!get().firebaseDoc) reject("No profile reference found")
      if (get().processing==true) reject("Processing") 
      updateProfile(profile,pic, get().firebaseDoc as any).then(result=>{
        set({profile:result})
        resolve(result)
      }).catch(error=> { console.log("error ", error); reject(error) })
    })
  },

})

export const useLoginState = useStaging 
  ? create(devtools(useLoginStore, { name: 'loginState' }))
  : create(useLoginStore) ;

const safeGtag = (eventName: string) => {
  try {
    if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
      (window as any).gtag("event", eventName);
    }
  } catch (e) {
    console.log('gtag error', e);
  }
};

const createProfileIfNotExists = async (firebaseData, mailingList, opts:ProfileOptions={}) => {
  const trackSignin = opts.trackSignin !== false;
  try {
    if(globalProfileIsProcessing==true) {
      return;
    }
    globalProfileIsProcessing = true;

    if ( (!firebaseData?.user?.uid) || (!firebaseData?.user.email)) {
      return null;
    }
    
    const uid = firebaseData.user.uid;
    const email = firebaseData.user.email;

    // this shouldn't be null. if it is, that would signal a development error
    //   The collection call is a synchronous, local operation that should 
    //   not require this check.
    // if (profilesDB==null) throw new Error("profilesDB is not defined")

    const q = query(profilesDB, where("uid", "==", uid));
    const querySnapshot = await getDocsFromServer(q);    

    if (querySnapshot.docs.length > 0) {
      const profile = querySnapshot.docs[0].data() as ProfileInterface
      if(!profile?.country) {
        const newProfile: ProfileInterface = await getUserCountry(querySnapshot);
        if (trackSignin) {
          trackEvent("signin", { source: getProviderSource(firebaseData) });
          safeGtag("signin");
        }
        return {ref:querySnapshot.docs[0].ref, updated:false, profile:newProfile}
      }
      if (trackSignin) {
        trackEvent("signin", { source: getProviderSource(firebaseData) });
        safeGtag("signin");
      }
      return {ref:querySnapshot.docs[0].ref, updated:false, profile:profile}
    }
    else {
      let country:any = 'United States'
      try {       
        const resp:any= await getUserLocationFromIPdata(2000);  
        country = resp?.country_name || 'United States'
      }
      catch (err) { 
        console.log('Could not get user location'); 
      }      
      
      // Handle incoming data from different providers
      let first_name = '';
      let last_name = '';
      
      switch (firebaseData?._tokenResponse?.providerId) {
        case 'google.com':
          first_name = firebaseData?._tokenResponse?.firstName;
          last_name = firebaseData?._tokenResponse?.lastName;
          break;
        case 'microsoft.com':
          first_name = firebaseData?._tokenResponse?.firstName;
          last_name = firebaseData?._tokenResponse?.lastName;
          break;
        default:
          break;
      }
      
      if (!uid || !email ) { throw new Error("uid and email are required"); }
      if (uid?.length < 5 || email?.length < 3) { throw new Error("uid and email are required"); }  
  
      const measuringCategoryValue = country == 'United States'?  MeasuringCategory.IMPERIAL: MeasuringCategory.METRIC

      const profile:ProfileInterface = {
        // TODO: move to using uid in the id field
        // bigger refactor; ensure we use uid for everything. like, ev ah ree ting.
        id:generateID(), 
        uid:uid, 
        email:email,country:country,
        measuring_category:measuringCategoryValue,
        measuringCategory:measuringCategoryValue,
        renders:0,
        first_name:first_name,
        last_name:last_name,
        mailingList:mailingList,
        signupSurface: opts.surface ?? 'web',
        createdAt:new Date(),
        created_at:new Date()
      }
      if(first_name?.length > 1) profile['first_name'] = first_name;
      if(last_name?.length > 1) profile['last_name'] = last_name;
      
      //create ref here. 
      const firebaseRef = doc(firebaseDB as any, 'profiles', profile.id as string)
      const result = await updateProfile(profile,{},firebaseRef)
      // A showroom that opened the sign-up modal stamped where it started
      // (Home/showrooms/signupOrigin.ts); the event carries it in both
      // destinations so sign-ups can be attributed to a showroom.
      const signupProps = {
        source: getProviderSource(firebaseData),
        ...(opts.surface ? { surface: opts.surface } : {}),
        ...(takeSignupOrigin() ?? {}),
      };
      // Identify first, so the signup event carries the GA4 user_id.
      identifyUser({ currentUser: firebaseData.user, profile });
      trackEvent("signup", signupProps);
      trackEventGA("signup", signupProps);
      trackEventMeta("CompleteRegistration", { content_name: getProviderSource(firebaseData) }, `signup_${uid}`);
      // New signup: seed starter content (staging AND prod, idempotent, backend-owned).
      // Fire-and-forget — never block or fail profile creation.
      seedStarterContent(uid);
      return {ref:firebaseRef,  updated:true, profile:result as ProfileInterface}
      }
    }
    catch (e) { 
      // TODO: push this to Sentry
      console.log(e) 
    }
    finally { 
      globalProfileIsProcessing = false;
    }
}

const getProfile = async (firebaseData) => {
  try {
    if (globalProfileIsProcessing==true) { return;  }
    globalProfileIsProcessing = true;
    if ( (!firebaseData?.user?.uid) || (!firebaseData?.user.email)) return null;   
    const uid = firebaseData.user.uid;
    if (profilesDB==null) throw new Error("profilesDB is not defined")
    const q = query(profilesDB, where("uid", "==", uid));
    const querySnapshot = await getDocs(q);    
    // console.log('how many times here?');
    if (querySnapshot.docs.length > 0) { 
      const profile = querySnapshot.docs[0].data() as ProfileInterface
      if(!profile?.country) {
        const newProfile: ProfileInterface = await getUserCountry(querySnapshot);        
        return {ref:querySnapshot.docs[0].ref, updated:false, profile:newProfile} 
      }
      return {ref:querySnapshot.docs[0].ref, updated:false, profile:querySnapshot.docs[0].data() as ProfileInterface} 
    }
    else return null;
  }
  finally {
    globalProfileIsProcessing = false;
  }
}

const getUserCountry = async (querySnapshot: any) => {
  const profile = querySnapshot.docs[0].data() as ProfileInterface;
  let country:any = 'United States'
  try {       
    const resp:any= await getUserLocationFromIPdata(2000);  
    country = resp?.country_name || 'United States'
    const newProfile:ProfileInterface = produce(profile, (draft:ProfileInterface) => {
      draft.country = country
    })
    await updateProfile(newProfile,{},querySnapshot.docs[0].ref)
    return newProfile;
  } catch (err) { console.log('Could not get user location'); }
  return profile;
}
